import { createElement, lazy } from "react";

// Wraps React.lazy so a failed dynamic import recovers gracefully:
//  - retries once for a transient blip,
//  - if still failing while online, it's likely a stale chunk after a deploy -> reload once,
//  - if offline, rethrows so ChunkErrorBoundary can show the offline fallback,
//    then replaces the rejected lazy component on the next online render.
const RELOAD_GUARD = "acton:chunkReloaded";

export function lazyWithRetry(importer) {
    let failedOffline = false;
    const createLazyComponent = () => lazy(async () => {
        try {
            const mod = await importer();
            sessionStorage.removeItem(RELOAD_GUARD);
            return mod;
        } catch (error) {
            try {
                await new Promise((resolve) => setTimeout(resolve, 500));
                const mod = await importer();
                sessionStorage.removeItem(RELOAD_GUARD);
                return mod;
            } catch (retryError) {
                if (window.navigator.onLine && !sessionStorage.getItem(RELOAD_GUARD)) {
                    sessionStorage.setItem(RELOAD_GUARD, "1");
                    window.location.reload();
                    // Hold the render while the page reloads with fresh assets.
                    return new Promise(() => { });
                }
                failedOffline = !window.navigator.onLine;
                throw retryError;
            }
        }
    });

    let LazyComponent = createLazyComponent();

    return function RetryableLazyComponent(props) {
        // React.lazy caches rejections, even after the error boundary is reset.
        if (failedOffline && window.navigator.onLine) {
            failedOffline = false;
            LazyComponent = createLazyComponent();
        }
        return createElement(LazyComponent, props);
    };
}
